/**
 * Pure helpers for CategoryCombobox. Kept separate from the component so the
 * matching / create-option rules are unit-testable without a DOM.
 */
import { buildCategoryTree, type Category, type CategoryWithChildren } from '@/types/category';

/** One level of indent in a category filter: non-breaking, so the browser keeps it. */
export const CATEGORY_INDENT = '\u00a0\u00a0\u00a0';

/** Trim + collapse inner whitespace so " Home  Care " and "Home Care" collide. */
export function normalizeCategoryName(value: string): string {
  return value.trim().replace(/\s+/g, ' ');
}

/** Case-insensitive comparison on the normalized form. */
export function isSameCategory(a: string, b: string): boolean {
  return normalizeCategoryName(a).toLowerCase() === normalizeCategoryName(b).toLowerCase();
}

/** Categories containing the query, case-insensitive. Empty query = all. */
export function filterCategories(categories: string[], query: string): string[] {
  const q = normalizeCategoryName(query).toLowerCase();
  if (!q) return categories;
  return categories.filter((c) => c.toLowerCase().includes(q));
}

/**
 * The typed name to offer as "+ Create ..." — or null when there is nothing to
 * create. Suppressed for a blank query and when the name already exists (an
 * exact match should be picked, not duplicated).
 */
export function createOptionFor(categories: string[], query: string): string | null {
  const name = normalizeCategoryName(query);
  if (!name) return null;
  if (categories.some((c) => isSameCategory(c, name))) return null;
  return name;
}

/**
 * The category filter's options (C13, D-G2): "All Categories", then the
 * category rows as a tree — each subcategory under its parent, one indent
 * deeper, siblings by name — then the names no row covers (legacy free text),
 * flat. The value is the plain name: the API matches by name and takes the
 * subcategories in too. A name already listed keeps its first place.
 */
export function categoryFilterOptions(
  rows: Category[],
  legacyNames: string[],
): { value: string; label: string }[] {
  const options = [{ value: '', label: 'All Categories' }];
  const seen = new Set<string>();
  const add = (name: string, depth: number) => {
    if (seen.has(name)) return;
    seen.add(name);
    options.push({ value: name, label: CATEGORY_INDENT.repeat(depth) + name });
  };
  const walk = (nodes: CategoryWithChildren[], depth: number) => {
    for (const node of [...nodes].sort((a, b) => a.name.localeCompare(b.name))) {
      add(node.name, depth);
      walk(node.children, depth + 1);
    }
  };
  walk(buildCategoryTree(rows), 0);
  // A row the tree never reaches (a parent loop) and the legacy names, flat.
  [...rows.map((r) => r.name), ...legacyNames]
    .sort((a, b) => a.localeCompare(b))
    .forEach((name) => add(name, 0));
  return options;
}
